/*
 * AnimatedCard — Kenney Playing Cards Pack (CC0)
 * BetUdontBet · GRUDGE STUDIO
 *
 * Asset path: /cards/card_{suit}_{rank}.png
 *   suit  → hearts | diamonds | clubs | spades
 *   rank  → A | 02–10 | J | Q | K
 *   back  → /media/card-back-budb.jpg (BUDB monogram logo back)
 */

import { motion, AnimatePresence } from 'framer-motion';
import { memo, useMemo, useState, useEffect } from 'react';

export interface Card {
  /** Full lowercase suit from server: hearts | diamonds | clubs | spades */
  suit: string;
  /** Rank from server: A | 2–10 | J | Q | K */
  value: string;
}

// ── Asset URL builders ────────────────────────────────────────────────────────

/** poker2 pack: {Suit}_{Rank}.png — professional illustrated style (primary) */
function cardSrcP2(suit: string, rank: string): string {
  const suitMap: Record<string, string> = {
    hearts: 'Hearts', heart: 'Hearts', h: 'Hearts',
    diamonds: 'Diamonds', diamond: 'Diamonds', d: 'Diamonds',
    clubs: 'Club', club: 'Club', c: 'Club',   // poker2 uses "Club" not "Clubs"
    spades: 'Spades', spade: 'Spades', s: 'Spades',
  };
  const rankMap: Record<string, string> = {
    '1': 'A', 'ace': 'A', 'jack': 'J', 'queen': 'Q', 'king': 'K',
  };
  const suitNorm = suitMap[suit.toLowerCase()] ?? 'Spades';
  const rankRaw  = rank.toUpperCase();
  const rankNorm = rankMap[rank.toLowerCase()] ?? rankRaw;

  // Spades_K has double-period in source file — preserved exactly
  if (suitNorm === 'Spades' && rankNorm === 'K') return '/cards2/Spades_K..png';
  return `/cards2/${suitNorm}_${rankNorm}.png`;
}

/** Kenney pack: card_{suit}_{rank}.png — clean vector style (fallback) */
function cardSrcKenney(suit: string, rank: string): string {
  const suitMap: Record<string, string> = {
    hearts: 'hearts', heart: 'hearts', h: 'hearts',
    diamonds: 'diamonds', diamond: 'diamonds', d: 'diamonds',
    clubs: 'clubs', club: 'clubs', c: 'clubs',
    spades: 'spades', spade: 'spades', s: 'spades',
  };
  const rankMap: Record<string, string> = { '1': 'A', 'ace': 'A', 'jack': 'J', 'queen': 'Q', 'king': 'K' };
  const suitNorm = suitMap[suit.toLowerCase()] ?? 'spades';
  const rankRaw  = rank.toUpperCase();
  const rankNorm = rankMap[rank.toLowerCase()] ?? rankRaw;
  const n = parseInt(rankNorm, 10);
  const rankPadded = (!isNaN(n) && n >= 2 && n <= 9) ? `0${n}` : rankNorm;
  return `/cards/card_${suitNorm}_${rankPadded}.png`;
}

/** Public API — resolves to active style, Kenney as img onerror fallback */
export function cardImageSrc(suit: string, rank: string, style: CardStyle = ACTIVE_CARD_STYLE): string {
  return style === 'p2' ? cardSrcP2(suit, rank) : cardSrcKenney(suit, rank);
}

/** Kenney fallback src for onerror handlers */
export function cardImageFallback(suit: string, rank: string): string {
  return cardSrcKenney(suit, rank);
}

// ── Card style config ─────────────────────────────────────────────────────────
// "p2"  = poker2 pack  (professional illustrated deck, primary)
// "kny" = Kenney pack  (clean vector deck, fallback)
export type CardStyle = 'p2' | 'kny';
export const ACTIVE_CARD_STYLE: CardStyle = 'p2';

/**
 * Production card back (gilded monogram + suit pattern).
 * Primary: media/card-back-production.jpg · fallbacks monogram / pack backs.
 */
export const CARD_BACK = '/media/card-back-production.jpg';
export const CARD_BACK_PRIMARY = CARD_BACK;
/** Legacy monogram */
export const CARD_BACK_BUDB = '/media/card-back-budb.jpg';
/** Same art under cards2 path (deck pack naming) */
export const CARD_BACK_P2 = '/cards2/shirt_budb.jpg';
/** Legacy poker2 backs (fallback only) */
export const CARD_BACK_BLUE = '/cards2/shirt_blue.png';
export const CARD_BACK_RED = '/cards2/shirt_red.png';
export const CARD_EMPTY = '/cards/card_empty.png'; // Kenney empty slot

// ── Size presets ─────────────────────────────────────────────────────────────
const SIZE = {
  xs:  { w: 34,  h: 48,  r: 4, shadow: '0 2px 6px rgba(0,0,0,0.55)'  },
  sm:  { w: 48,  h: 67,  r: 5, shadow: '0 3px 10px rgba(0,0,0,0.55)' },
  md:  { w: 68,  h: 95,  r: 6, shadow: '0 5px 18px rgba(0,0,0,0.55)' },
  lg:  { w: 86,  h: 120, r: 8, shadow: '0 7px 28px rgba(0,0,0,0.60)' },
} as const;
type SizeKey = keyof typeof SIZE;

// ── Dealing animation presets ─────────────────────────────────────────────────
const DEAL_VARIANTS = {
  hidden:  { scale: 0.5, opacity: 0, y: -80, rotate: -15 },
  visible: (delay: number) => ({
    scale: 1, opacity: 1, y: 0, rotate: 0,
    transition: { type: 'spring', stiffness: 300, damping: 24, delay: delay * 0.1 },
  }),
  exit: { scale: 0.7, opacity: 0, y: 24, transition: { duration: 0.15 } },
};

// ── Single card ───────────────────────────────────────────────────────────────
interface AnimatedCardProps {
  card?: Card | null;
  /** Show card back (face-down). Triggers 3-D flip when toggled. */
  isHidden?: boolean;
  /** Stagger index for deal animation */
  delay?: number;
  size?: SizeKey;
  /** Play deal-in animation on mount */
  isNew?: boolean;
  className?: string;
}

export const AnimatedCard = memo(function AnimatedCard({
  card,
  isHidden = false,
  delay = 0,
  size = 'md',
  isNew = false,
  className = '',
}: AnimatedCardProps) {
  const { w, h, r, shadow } = SIZE[size];
  const [frontSrc, setFrontSrc] = useState(() =>
    card ? cardImageSrc(card.suit, card.value) : CARD_BACK
  );

  // Keep frontSrc in sync if card prop changes
  useEffect(() => {
    setFrontSrc(card ? cardImageSrc(card.suit, card.value) : CARD_BACK);
  }, [card?.suit, card?.value]);

  const showFront = card && !isHidden;

  // Placeholder empty slot
  if (!card && !isHidden) {
    return (
      <div
        className={className}
        data-testid="card-placeholder"
        style={{
          width: w, height: h, borderRadius: r,
          border: '2px dashed rgba(255,255,255,0.13)',
          background: 'rgba(255,255,255,0.03)',
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={`${card?.suit ?? 'back'}-${card?.value ?? 'x'}-${isHidden}`}
        custom={delay}
        variants={DEAL_VARIANTS}
        initial={isNew ? 'hidden' : false}
        animate="visible"
        exit="exit"
        className={className}
        data-testid={card && !isHidden ? `card-${card.value}-${card.suit}` : 'card-back'}
        style={{ width: w, height: h, flexShrink: 0, perspective: 700 }}
      >
        {/* 3-D flip container */}
        <motion.div
          animate={{ rotateY: isHidden ? 180 : 0 }}
          transition={{ duration: 0.38, delay: delay * 0.05 }}
          style={{
            width: '100%', height: '100%',
            position: 'relative',
            transformStyle: 'preserve-3d',
          }}
        >
          {/* Front face */}
          <img
            src={frontSrc}
            alt={card && !isHidden ? `${card.value} of ${card.suit}` : 'card'}
            width={w} height={h}
            style={{
              position: 'absolute', inset: 0,
              width: w, height: h,
              objectFit: 'contain',
              borderRadius: r,
              boxShadow: shadow,
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              display: 'block',
            }}
            onError={() => {
              // poker2 failed → try Kenney fallback
              if (card && frontSrc !== cardImageFallback(card.suit, card.value)) {
                setFrontSrc(cardImageFallback(card.suit, card.value));
              }
            }}
          />
          {/* Back face (rotated 180°, shown when isHidden=true) — BUDB monogram */}
          <img
            src={CARD_BACK}
            alt="BUDB PKR card back"
            width={w} height={h}
            style={{
              position: 'absolute', inset: 0,
              width: w, height: h,
              objectFit: 'cover',
              borderRadius: r,
              boxShadow: shadow,
              border: '1px solid rgba(212,175,55,0.35)',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
              display: 'block',
              background: '#050a14',
            }}
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              if (img.src.indexOf('card-back-production') >= 0) {
                img.src = CARD_BACK_BUDB;
              } else if (img.src.indexOf('card-back-budb') >= 0) {
                img.src = CARD_BACK_P2;
              } else if (img.src.indexOf('shirt_budb') < 0) {
                img.src = CARD_BACK_P2;
              } else if (img.src.indexOf('shirt_blue') < 0) {
                img.src = CARD_BACK_BLUE;
              }
            }}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
});

// ── Community Cards (board) ───────────────────────────────────────────────────
interface CommunityCardsProps {
  cards: Card[];
  isDealing?: boolean;
  newCardIndices?: number[];
}

export const CommunityCards = memo(function CommunityCards({
  cards,
  isDealing = false,
  newCardIndices = [],
}: CommunityCardsProps) {
  const placeholders = useMemo(() => Array(5 - cards.length).fill(null), [cards.length]);

  return (
    <motion.div
      className="flex gap-2 justify-center items-center"
      data-testid="community-cards"
      layout
    >
      {cards.map((card, i) => (
        <AnimatedCard
          key={`board-${i}-${card.suit}-${card.value}`}
          card={card}
          size="lg"
          delay={i}
          isNew={newCardIndices.includes(i) || isDealing}
        />
      ))}
      {placeholders.map((_, i) => (
        <AnimatedCard key={`board-ph-${cards.length + i}`} size="lg" />
      ))}
    </motion.div>
  );
});

// ── Hole Cards (player hand) ──────────────────────────────────────────────────
interface HoleCardsProps {
  cards: Card[];
  /** If true, show card back face-down with flip-reveal capability */
  isHidden?: boolean;
  isDealing?: boolean;
  size?: SizeKey;
}

export const HoleCards = memo(function HoleCards({
  cards,
  isHidden = false,
  isDealing = false,
  size = 'sm',
}: HoleCardsProps) {
  // Empty hand: two placeholder back cards while waiting for deal
  if (cards.length === 0) {
    return (
      <motion.div
        className="flex gap-1"
        data-testid="hole-cards-empty"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
      >
        <img src={CARD_BACK} alt="BUDB card back" width={SIZE[size].w} height={SIZE[size].h}
          style={{ borderRadius: SIZE[size].r, opacity: 0.45, objectFit: 'cover', flexShrink: 0, background: '#050a14' }} />
        <img src={CARD_BACK} alt="BUDB card back" width={SIZE[size].w} height={SIZE[size].h}
          style={{ borderRadius: SIZE[size].r, opacity: 0.45, objectFit: 'cover', flexShrink: 0, background: '#050a14' }} />
      </motion.div>
    );
  }

  return (
    <motion.div
      className="flex gap-1"
      data-testid="hole-cards"
      initial={isDealing ? { scale: 0.6, opacity: 0, y: -20 } : false}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 320, damping: 26 }}
    >
      {cards.map((card, i) => (
        <AnimatedCard
          key={`hole-${i}`}
          card={card}
          isHidden={isHidden}
          delay={i}
          size={size}
          isNew={isDealing}
        />
      ))}
    </motion.div>
  );
});

// ── Folded card fan (visual only, two face-down cards fanned) ─────────────────
interface FoldedCardsProps { size?: SizeKey; }

export const FoldedCards = memo(function FoldedCards({ size = 'sm' }: FoldedCardsProps) {
  const { w, h, r } = SIZE[size];
  return (
    <div style={{ position: 'relative', width: w + 12, height: h }}>
      {['-10deg', '10deg'].map((angle, i) => (
        <img
          key={i}
          src={CARD_BACK}
          alt="folded card"
          width={w} height={h}
          style={{
            position: 'absolute',
            top: 0,
            left: i * 8,
            width: w, height: h,
            objectFit: 'cover',
            borderRadius: r,
            transform: `rotate(${angle})`,
            opacity: 0.55,
            background: '#050a14',
          }}
        />
      ))}
    </div>
  );
});
